import React, {useCallback, useEffect} from "react";
import {Button, Flex, Segmented} from "antd";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faDesktop, faMobileScreen, faPlay, faStop} from "@fortawesome/free-solid-svg-icons";
import {Shot, Combined, Argument, Action} from "../../../feature/project/Shot";
import {SceneElements} from "./SceneElements";
import {animationToTransformFrom, animationToTransformTo} from "../../common/Animation";
import {AnimationType} from "../../common/Animations";
import {LocationElement} from "./LocationElement";
import {useAppSelector} from "../../../feature/tools";
import {NarrationElement} from "./NarrationElement";
import {ChoicesElement} from "./ChoicesElement";
import {AudioElement} from "./AudioElement";

export enum ShotState {
    Idle = 'IDLE',
    Starting = 'STARTING',
    Playing = 'PLAYING',
    Resuming = 'RESUMING',
    Stopping = 'STOPPING',
}

interface TransitionState {
    transform: string,
    opacity: number,
    type: AnimationType | undefined,
    state: ShotState,
}

export const ShotViewport = (props: {
    editable: boolean,
    viewportSize: [height: number, width: number],
    shot: Shot | undefined,
    element: Combined | undefined,
    onElementSelected: (element: Combined | undefined) => void,
    onLocationSelected: () => void,
    onElementSettingsClick: (show: boolean) => void,
    elementToolbar: React.ReactNode,
    onMove?: (info: { x: number, y: number, z: number, scale: number }) => void,
}) => {
    const initialState = {transform: '', opacity: 1, type: undefined, state: ShotState.Idle}

    const [viewMode, setViewMode] = React.useState('desktop')
    const [height, setHeight] = React.useState(0)
    const [width, setWidth] = React.useState(0)
    const [scale, setScale] = React.useState(1)
    const [state, setState] = React.useState<TransitionState>(initialState)
    const [played, setPlayed] = React.useState(new Set<string>())
    const [isPlaying, setIsPlaying] = React.useState(false)
    const configNarration = useAppSelector(state => state.projects.configNarration[0])//[viewMode === 'desktop' ? 0 : 1])
    const configChoice = useAppSelector(state => state.projects.configChoice[0])//[viewMode === 'desktop' ? 0 : 1])
    const [soundActions, setSoundActions] = React.useState<Action[]>([])

    useEffect(() => {
        if (props.shot) {
            setSoundActions(extractSoundAction(props.shot))
        }
    }, [props.shot])

    useEffect(() => {
        if (state.state === ShotState.Starting && state.type === AnimationType.TransitionIn) {
            delay(300).then(() => {
                setState({
                    ...state,
                    type: AnimationType.TransitionIn,
                    transform: animationToTransformTo(props.shot?.transitionIn),
                    opacity: props.shot?.transitionIn?.alphaTo !== undefined && props.shot?.transitionIn?.alphaTo !== null ? props.shot?.transitionIn?.alphaTo : 1,
                    state: ShotState.Playing
                })
            })
        }
        if (state.state === ShotState.Starting && state.type === AnimationType.TransitionOut) {
            delay(300).then(() => {
                setState({
                    ...state,
                    type: AnimationType.TransitionOut,
                    transform: animationToTransformTo(props.shot?.transitionOut),
                    opacity: props.shot?.transitionOut?.alphaTo !== undefined && props.shot?.transitionOut?.alphaTo !== null ? props.shot?.transitionOut?.alphaTo : 1,
                    state: ShotState.Resuming
                })
            })
        } else if (state.type === AnimationType.TransitionOut && state.state === ShotState.Idle) {
            delay(500).then(() => {
                setState(initialState)
            })
        } else if (state.type === AnimationType.TransitionIn && state.state === ShotState.Playing) {
            setState({
                ...state,
                state: ShotState.Stopping
            })
        } else if (state.type === AnimationType.TransitionOut && state.state === ShotState.Resuming) {
            setState({
                ...state,
                state: ShotState.Stopping
            })
        } else if (played.size === 4) {
            setPlayed(new Set())
            delay(300).then(() => {
                setState({
                    ...state,
                    type: AnimationType.TransitionOut,
                    transform: animationToTransformFrom(props.shot?.transitionOut),
                    opacity: props.shot?.transitionOut?.alphaFrom !== undefined && props.shot?.transitionOut?.alphaFrom !== null ? props.shot?.transitionOut?.alphaFrom : 1,
                    state: ShotState.Starting
                })
            })
        }
    }, [state.state, state.type, played])

    useEffect(() => {
        setIsPlaying(state.state !== ShotState.Idle)
    }, [state.state])

    useEffect(() => {
        setScale(width > height ? width / 1920 : height / 1920)
    }, [width, height, props.element])


    const elementRef = useCallback((node: HTMLDivElement) => {
        if (!node) return;
        const resizeObserver = new ResizeObserver(() => {
            setWidth(node.clientWidth || 0)
            setHeight(node.clientHeight || 0)
        });
        resizeObserver.observe(node);
    }, []);

    return (
        <div
            ref={elementRef}
            onTransitionEnd={async () => {
                if (state.type === AnimationType.TransitionOut && state.state === ShotState.Stopping) {
                    setState({
                        ...state,
                        state: ShotState.Idle
                    })
                }
            }}
            style={{
                position: "relative",
                background: '#000',
                height: viewMode !== 'desktop' ? props.viewportSize[0] : props.viewportSize[1] / (1920/1080),
                width: viewMode !== 'desktop' ? props.viewportSize[0] / (1920/1080) : props.viewportSize[1],
                overflow: 'hidden',
            }}>
            <Button icon={<FontAwesomeIcon icon={state.type !== undefined ? faStop : faPlay}/>}
                    style={{lineHeight: '12px', position: 'absolute', top: 10, right: 90, zIndex: 10000}}
                    onClick={async () => {
                        if (state.state === ShotState.Idle) {
                            setPlayed(new Set())
                            setState({
                                type: AnimationType.TransitionIn,
                                transform: animationToTransformFrom(props.shot?.transitionIn),
                                opacity: props.shot?.transitionIn?.alphaFrom !== undefined && props.shot?.transitionIn?.alphaFrom !== null ? props.shot?.transitionIn?.alphaFrom : 1,
                                state: ShotState.Starting,
                            })
                        } else {
                            setState(initialState)
                            setPlayed(new Set())
                        }
                    }}
            />
            <Segmented
                style={{position: 'absolute', top: 10, right: 10, zIndex: 10000}}
                options={
                    [
                        {value: 'desktop', label: <FontAwesomeIcon icon={faDesktop}/>},
                        {value: 'mobile', label: <FontAwesomeIcon icon={faMobileScreen}/>},
                    ]
                }
                onChange={(value) => setViewMode(value)}/>

            {props.elementToolbar}
            {scale > 0 ?
                <Flex
                    align={'center'}
                    justify={'center'}
                    id={"viewport"}
                    onMouseDown={() => props.onElementSelected(undefined)}
                    style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: viewMode == 'desktop' ? '1920px' : '1080px',
                        height: viewMode == 'desktop' ? '1080px' : '1920px',
                        transform: state.transform,
                        opacity: state.opacity,
                        transitionProperty: isPlaying ? 'transform, opacity' : 'none',
                        transitionDuration: `${props.shot?.transitionIn?.duration}ms` || `0ms`,
                        transitionDelay: `${props.shot?.transitionIn?.startDelay}ms` || `0ms`,
                        transitionTimingFunction: `${props.shot?.transitionIn?.interpolator}` || `ease`,
                        transformOrigin: 'top left',
                        overflow: 'hidden',
                        scale: `${scale}`
                    }}>
                    <LocationElement
                        editable={props.editable}
                        prefix={'viewport-location'}
                        location={props.shot?.location}
                        play={state.state !== ShotState.Idle}
                        onPlayEnd={(id: number) => {
                            setPlayed(prev => new Set(prev).add(`Location`))
                        }}
                        onMove={props.onMove}
                        onElementSelected={props.onLocationSelected}
                    />

                    <SceneElements
                        editable={props.editable}
                        prefix={'viewport-characters'}
                        elements={props.shot?.characters || []}
                        play={state.state !== ShotState.Idle}
                        onPlayEnd={(type: string) => {
                            setPlayed(prev => new Set(prev).add(type))
                        }}
                        type={'Character'}
                        selectedElement={props.element}
                        scale={scale}
                        onMove={props.onMove}
                        onElementSelected={props.onElementSelected}
                    />

                    <SceneElements
                        editable={props.editable}
                        prefix={'viewport-objects'}
                        elements={props.shot?.objects || []}
                        type={'Object'}
                        play={state.state !== ShotState.Idle}
                        onPlayEnd={(type: string) => {
                            setPlayed(prev => new Set(prev).add(type))
                        }}
                        selectedElement={props.element}
                        scale={scale}
                        onMove={props.onMove}
                        onElementSelected={props.onElementSelected}
                    />
                    {props.shot?.narration && props.shot.narration.text ?
                        <NarrationElement
                            editable={props.editable}
                            configNarration={configNarration}
                            narration={props.shot?.narration}
                            scale={scale}
                            onPlayEnd={(type: string) => {
                                setPlayed(prev => new Set(prev).add(type))
                            }}
                            isSelected={props.element?.type === 'Narrative'}
                            isPlaying={isPlaying}
                            onElementSelected={
                                props.onElementSelected
                            }/> : ''}
                    {(props.shot?.narration?.choices?.length ?? 0) > 0 ?
                        <ChoicesElement
                            editable={props.editable}
                            configChoices={configChoice}
                            choices={props.shot?.narration?.choices ?? []}
                            onPlayEnd={(type: string) => {
                                setPlayed(prev => new Set(prev).add(type))
                            }}
                            scale={scale}
                            isSelected={props.element?.type === 'Choice'}
                            isPlaying={isPlaying}
                            onElementSelected={
                                props.onElementSelected
                            }/> : ''}
                    {soundActions.length > 0 ?
                        <AudioElement
                            actions={soundActions}
                            onPlayEnd={(type: string) => {
                                setPlayed(prev => new Set(prev).add(type))
                            }}
                            isPlaying={isPlaying}
                        /> : ''}
                </Flex> : ''}
        </div>
    );
}

function extractSoundAction(shot: Shot): Action[] {
    return shot?.actions?.filter((action: Action) => action.arguments.filter((arg: Argument) => arg.title == "playSound").length > 0) ?? []
}

export const delay = (ms: number) => {
    return new Promise(resolve => setTimeout(resolve, ms));
};

export function getFontWeight(weight: "normal" | "bold" | "semi-bold" | "light" | "thin" | undefined): number {
    return weight === 'bold' ? 700 :
        weight === 'semi-bold' ? 500 :
            weight === 'normal' ? 400 :
                weight === 'light' ? 300 :
                    weight === 'thin' ? 300 : 400
}